import React, { useEffect, useState } from 'react'
import Header from '../../components/pageHeader'
import config from './config'
import axios from 'axios'

import styles from './insights.module.scss'

import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from 'recharts'
import baseconfig from '../../../../variables/config'
import moment from 'moment'
// COMPONENTS
import TabButtons from '../../components/TabButtons'
import DateRange from '../../components/DateRange'
import InsightsPieChart from './InsightsPieChart'
import PartyStatsDisplay from './PartyStatsDisplay'
import {
  computePartyInsights,
  emptyPartyStats,
  mergeById,
  sumVisitorAgeBuckets
} from './insightsStats'

const enrichMemberSessions = (sessions, users, timeKey) => {
  if (!sessions || sessions.length === 0) return []
  sessions.forEach((session, i) => {
    users.forEach(user => {
      if (session.wKUserId === user.id) {
        session.username = user.firstName + ' ' + user.lastName
        session.bookingNo = i
        const timeStr = session[timeKey] || ''
        let timeTemp = timeStr.substring(0, 2)
        if (timeTemp === '09') {
          timeTemp = '9'
        }
        session.timeslot = parseInt(timeTemp, 10) || 0
      }
    })
  })
  return sessions
}

const isStaffUser = () => {
  try {
    const { role } = JSON.parse(window.localStorage.getItem('userData'))
    return role === 'staff'
  } catch {
    return false
  }
}

const Insights = () => {
  // STATE
  const [unauthorised] = useState(isStaffUser)
  const [tab, setTab] = useState(0)
  const [tabs, setTabs] = useState([])
  const [bookingInsightsTab, setBookingInsightsTab] = useState(0)
  const [analysisBookingTab, setAnalysisBookingTab] = useState(0)
  const [playAgeStats, setPlayAgeStats] = useState({})
  const [partyStats, setPartyStats] = useState(emptyPartyStats())
  const [playMemberBookings, setPlayMemberBookings] = useState([])
  const [playMemberUsers, setPlayMemberUsers] = useState([])
  const [playMemberList, setPlayMemberList] = useState([])
  const [members, setMembers] = useState([])
  const [selected, setSelected] = useState(null)
  const [selectedMember, setSelectedMember] = useState({})
  const [switchDate, setSwitchDate] = useState(moment())

  const [activeTab, setActiveTab] = useState(0)
  const [startDate1, setStartDate1] = useState('')
  const [endDate1, setEndDate1] = useState('')
  const [startDate2, setStartDate2] = useState('')
  const [endDate2, setEndDate2] = useState('')
  const [count1, setCount1] = useState(0)
  const [count2, setCount2] = useState(0)
  const [duration1, setDuration1] = useState(0)
  const [duration2, setDuration2] = useState(0)
  // EFFECTS
  useEffect(() => {
    if (unauthorised) return
    const cb = i => setTab(i)
    setTabs([{ name: '', cb: () => cb(0) }])
    fetchPlayBookings()
    fetchPlayMemberBookings()
    fetchPartyBookings()
    fetchMembers()
  }, [])

  // UTILS
  const insightTabs = ['General', 'Analysis', 'Members']

  const getOptions = (token, data, prop) => {
    let options = {
      query: {
        access_token: token
      }
    }
    if (data && data.date) {
      const endDate = data.date.clone().endOf('week')
      endDate.add(1, 'days')
      const startDate = data.date.clone().startOf('week')
      startDate.set({ hour: 22, minute: 59 })
      options.params = {
        filter: {
          where: {
            and: [
              { [prop]: { lt: new Date(endDate.format()) } },
              { [prop]: { gt: new Date(startDate.format()) } }
            ]
          },
          // Same field as the filter, so a date index can serve the query.
          order: prop + ' ASC'
        }
      }
    }
    return options
  }

  const fetchPlayBookings = async () => {
    let token = JSON.parse(window.localStorage.getItem('userData')).token
    const ops = getOptions(token, { date: switchDate }, 'date')
    const playPath =
      baseconfig.serverURL + '/PlayOnlyBooking' + baseconfig.endpoint
    let live = []
    // Temporary: /archive scans the whole historical collection and can pin the
    // API disk. Restore this once the date indexes exist.
    let archived = []
    try {
      const liveRes = await axios.get(playPath, ops)
      live = Array.isArray(liveRes.data) ? liveRes.data : []
    } catch (error) {
      console.log('insights - fetchPlayBookings:', error.message)
    }
    // try {
    //   const archRes = await axios.get(playPath + '/archive', ops)
    //   archived = Array.isArray(archRes.data) ? archRes.data : []
    // } catch (error) {
    //   console.log('insights - fetchPlayBookings archive:', error.message)
    // }
    const playOnlyBookings = mergeById(live, archived)

    playOnlyBookings.sort((playOne, playTwo) => {
      const dateOne = moment(playOne.date)
      const timeOne = moment(playOne.time.split(' - ')[0], 'h:mm A')
        .format('HH:mm')
        .split(':')

      dateOne.set({ h: Number(timeOne[0]), m: Number(timeOne[1]) })

      const dateTwo = moment(playTwo.date)
      const timeTwo = moment(playTwo.time.split(' - ')[0], 'h:mm A')
        .format('HH:mm')
        .split(':')

      dateTwo.set({ h: Number(timeTwo[0]), m: Number(timeTwo[1]) })
      if (dateOne.isBefore(dateTwo)) return -1
      if (dateOne.isBefore(dateTwo)) return 1
      return 0
    })
    let under12months = 0
    let oneYearOlds = 0
    let twoToThree = 0
    let fourAndOver = 0
    let adults = 0

    playOnlyBookings.forEach(booking => {
      const b = sumVisitorAgeBuckets(booking)
      under12months += b.under12months
      oneYearOlds += b.oneYearOlds
      twoToThree += b.twoToThree
      fourAndOver += b.fourAndOver
      adults += b.adults
    })

    setPlayAgeStats({
      under12months,
      oneYearOlds,
      twoToThree,
      fourAndOver,
      adults
    })
  }

  const fetchPartyBookings = async () => {
    let token = JSON.parse(window.localStorage.getItem('userData')).token
    const ops = getOptions(token, { date: switchDate }, 'partyDate')
    const partyPath =
      baseconfig.serverURL + '/PartyBooking' + baseconfig.endpoint
    let live = []
    // Temporary: /archive scans the whole historical collection and can pin the
    // API disk. Restore this once the date indexes exist.
    let archived = []
    try {
      const liveRes = await axios.get(partyPath, ops)
      live = Array.isArray(liveRes.data) ? liveRes.data : []
    } catch (error) {
      console.log('insights - fetchPartyBookings:', error.message)
    }
    // try {
    //   const archRes = await axios.get(partyPath + '/archive', ops)
    //   archived = Array.isArray(archRes.data) ? archRes.data : []
    // } catch (error) {
    //   console.log('insights - fetchPartyBookings archive:', error.message)
    // }
    let partyBookings = mergeById(live, archived)
    partyBookings = partyBookings.filter(
      party => party.reference !== 'WKPBReservation'
    )
    partyBookings.sort((partyOne, partyTwo) => {
      const dateOne = moment(partyOne.partyDate)
      const timeOne = moment(partyOne.partyTime.split(' - ')[0], 'h:mm A')
        .format('HH:mm')
        .split(':')
      dateOne.set({ h: Number(timeOne[0]), m: Number(timeOne[1]) })

      const dateTwo = moment(partyTwo.partyDate)
      const timeTwo = moment(partyTwo.partyTime.split(' - ')[0], 'h:mm A')
        .format('HH:mm')
        .split(':')
      dateTwo.set({ h: Number(timeTwo[0]), m: Number(timeTwo[1]) })

      if (dateOne.isBefore(dateTwo)) return -1
      if (dateOne.isBefore(dateTwo)) return 1
      return 0
    })

    setPartyStats(computePartyInsights(partyBookings))
  }

  const fetchPlayMemberBookings = async () => {
    let token = JSON.parse(window.localStorage.getItem('userData')).token
    const ops = getOptions(token, { date: switchDate }, 'date')
    const memberPath =
      baseconfig.serverURL +
      '/getPlayOnlyBookings/members/PlayOnlyBooking' +
      baseconfig.branch

    let bookings = { users: [], bookings: [] }
    try {
      const liveRes = await axios.get(memberPath, ops)
      const payload = liveRes.data || {}
      const liveBookings = Array.isArray(payload.bookings) ? payload.bookings : []
      // Temporary: /archive scans the whole historical collection and can pin the
      // API disk. Restore this once the date indexes exist.
      // const playPath =
      //   baseconfig.serverURL + '/PlayOnlyBooking' + baseconfig.endpoint
      // let archBookings = []
      // try {
      //   const archRes = await axios.get(playPath + '/archive', ops)
      //   const archList = Array.isArray(archRes.data) ? archRes.data : []
      //   archBookings = archList.filter(
      //     booking =>
      //       booking &&
      //       (booking.member ||
      //         (booking.note && booking.note.includes('Member Purchase')))
      //   )
      // } catch (e) {
      //   console.log('insights - fetchPlayMemberBookings archive:', e.message)
      // }
      // const memberBookings = mergeById(liveBookings, archBookings)
      bookings = {
        users: payload.users || [],
        bookings: liveBookings
      }
    } catch (error) {
      console.log('insights - fetchPlayMemberBookings:', error.message)
    }

    const users = bookings.users
    const sessions = bookings.bookings
    setPlayMemberBookings(sessions && sessions.length > 0 ? sessions : [])
    setPlayMemberUsers(users && users.length > 0 ? users : [])
    if (sessions && sessions.length > 0) {
      enrichMemberSessions(sessions, users, 'time')
    }
    setPlayMemberList(sessions && sessions.length > 0 ? sessions : [])
  }

  const fetchMembers = async () => {
    let token = JSON.parse(window.localStorage.getItem('userData')).token
    const members = (await axios.get(baseconfig.serverURL + '/Memberships'))
      .data
    setMembers(members)
  }

  const deleteMember = async member => {
    let token = JSON.parse(window.localStorage.getItem('userData')).token
    if (
      window.confirm(
        'Warning! This is not undoable. Please make sure you want to DELELE'
      )
    ) {
      const response = await axios.delete(
        baseconfig.serverURL + '/Memberships/' + member.id
      )
      console.log(response, 'response')
    }
  }

  const getMember = async (membership, i) => {
    let token = JSON.parse(window.localStorage.getItem('userData')).token
    let selectedUser = {}
    try {
      selectedUser = (await axios.get(
        baseconfig.serverURL +
          '/onsiteUsers/' +
          membership.userId +
          '?access_token=' +
          token
      )).data
      if (selectedUser && selectedUser.length > 0) {
        selectedUser = (await axios.get(
          baseconfig.serverURL +
            '/WKUsers/' +
            membership.userId +
            '?access_token=' +
            token
        )).data
      }
    } catch (error) {
      console.log('get user - fetchSubscription:', error.message)
    }
    setSelected(i)
    let image =
      baseconfig.serverURL +
      '/ImageUploads/wackykingdom/download/' +
      selectedUser.image +
      '?access_token=' +
      token

    try {
      if (
        membership &&
        membership.subscriptionId &&
        membership.subscriptionId !== ''
      ) {
        const params = {
          subscriptionId: membership.subscriptionId
        }

        const subscription = (await axios.post(
          baseconfig.serverURL + '/subscriptionGet' + '?access_token=' + token,
          params
        )).data
        const extraParams = {
          isActive: subscription.status === 'active',
          endDate: moment
            .unix(subscription.current_period_end)
            .format('DD/MM/YYYY')
        }
        setSelectedMember({
          ...selectedUser,
          imageFull: image,
          extraParams: extraParams
        })
      } else if (membership) {
        var startDate = moment(membership.startDate)
        startDate.add(12, 'M')

        const extraParams = {
          isActive: false,
          endDate: startDate.format('DD/MM/YYYY')
        }
        setSelectedMember({
          ...selectedUser,
          imageFull: image,
          extraParams: extraParams
        })
      }
    } catch (error) {
      console.log('checkmember - fetchSubscription:', error.message)
    }
  }

  const renderBooking = user => {
    const userBookings = playMemberBookings.filter(
      session => session.wKUserId === user.id
    )
    return (
      <div>
        <p>{user.firstName + ' ' + user.lastName}</p>
        {userBookings.map(booking => (
          <div>
            <p>Date: {booking.date.substring(0, 10)}</p>
            <p>Time: {booking.timesss}</p>
          </div>
        ))}
      </div>
    )
  }

  const playPieData = [
    { name: 'Adults', value: playAgeStats.adults },
    { name: 'Under 1', value: playAgeStats.under12months },
    { name: '1-2 YO', value: playAgeStats.oneYearOlds },
    { name: '2-3 YO', value: playAgeStats.twoToThree },
    { name: '4+ YO', value: playAgeStats.fourAndOver }
  ]

  const partyPieData = partyStats.partyTypes

  const activePieData =
    bookingInsightsTab === 0 ? playPieData : partyPieData

  const analysisBookingType =
    analysisBookingTab === 0 ? 'play' : 'party'
  console.log(selectedMember, 'selectm')
  return (
    <div className={`ordersContainer ${styles.Insights}`}>
      {unauthorised && (
        <div className="unauthorisedMessage">
          You are not authorised to view insights. Only administrators can
          access this page.
        </div>
      )}
      {!unauthorised && (
        <>
      <Header
        title={config.mainTitle}
        tip={config.subHeading}
        icon={config.titleIcon}
        tabs={tabs}
        selected={tab}
      />
      <div className={styles.TabsBtns}>
        {insightTabs.map((tab, index) => (
          <TabButtons
            key={index}
            tab={tab}
            index={index}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        ))}
      </div>
      {activeTab === 0 && (
        <div className={styles.insightTab}>
          <div className={styles.bookingTypeTabs}>
            <span>Booking type:</span>
            <button
              type="button"
              className={
                bookingInsightsTab === 0 ? styles.activeBookingType : ''
              }
              onClick={() => setBookingInsightsTab(0)}>
              Play only
            </button>
            <button
              type="button"
              className={
                bookingInsightsTab === 1 ? styles.activeBookingType : ''
              }
              onClick={() => setBookingInsightsTab(1)}>
              Party
            </button>
          </div>
          <InsightsPieChart data={activePieData} />
          <div>
            {bookingInsightsTab === 0 ? (
              <>
                <h3>Customers Age Group This Week:</h3>
                <p>Adults: {playAgeStats.adults}</p>
                <p>Under 12 months: {playAgeStats.under12months}</p>
                <p>One year Olds: {playAgeStats.oneYearOlds}</p>
                <p>Two to three:{playAgeStats.twoToThree}</p>
                <p>Four and Over: {playAgeStats.fourAndOver}</p>
              </>
            ) : (
              <PartyStatsDisplay
                partyStats={partyStats}
                title="Party overview this week"
              />
            )}
          </div>
          {bookingInsightsTab === 0 && (
            <>
              <h3>Members Booking This Week:</h3>
              <div className="insightTable">
                {playMemberUsers.map(user => (
                  <div key={user.id}>
                    <h3>{user.firstName + ' ' + user.lastName}</h3>
                    {playMemberBookings.map(booking => {
                      if (user.id === booking.wKUserId) {
                        return (
                          <p key={booking.id}>
                            {booking.date.substring(0, 10) +
                              ' ' +
                              booking.time}
                          </p>
                        )
                      }
                    })}
                  </div>
                ))}
              </div>
              <div className={styles.chartWrap}>
                <ScatterChart
                  width={800}
                  height={400}
                  margin={{
                    top: 20,
                    right: 20,
                    bottom: 20,
                    left: 20
                  }}>
                  <CartesianGrid />
                  <XAxis
                    type="number"
                    dataKey="bookingNo"
                    name="Member Booking"
                    unit=""
                  />
                  <YAxis
                    type="number"
                    dataKey="timeslot"
                    name="Timeslot"
                    unit="Hr"
                    domain={[9, 17]}
                  />
                  <Tooltip cursor={{ strokeDasharray: '2 2' }} />
                  <Scatter
                    name="Booking Time"
                    data={playMemberList}
                    fill="#8884d8"
                  />
                </ScatterChart>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 1 && (
        <div className={styles.insightTab}>
          <div className={styles.bookingTypeTabs}>
            <span>Booking type:</span>
            <button
              type="button"
              className={
                analysisBookingTab === 0 ? styles.activeBookingType : ''
              }
              onClick={() => setAnalysisBookingTab(0)}>
              Play only
            </button>
            <button
              type="button"
              className={
                analysisBookingTab === 1 ? styles.activeBookingType : ''
              }
              onClick={() => setAnalysisBookingTab(1)}>
              Party
            </button>
          </div>
          {count1 !== 0 && count2 !== 0 && duration1 > 0 && duration2 > 0 && (
            <button className={styles.averageRatio}>
              daily average ratio:{' '}
              {Math.round((100 * (count1 / duration1)) / (count2 / duration2))}%
            </button>
          )}
          <div className={styles.compareRanges}>
            <DateRange
              bookingType={analysisBookingType}
              startDate={startDate1}
              setStartDate={setStartDate1}
              endDate={endDate1}
              setEndDate={setEndDate1}
              count={count1}
              setCount={setCount1}
              duration={duration1}
              setDuration={setDuration1}
            />
            <DateRange
              bookingType={analysisBookingType}
              startDate={startDate2}
              setStartDate={setStartDate2}
              endDate={endDate2}
              setEndDate={setEndDate2}
              count={count2}
              setCount={setCount2}
              duration={duration2}
              setDuration={setDuration2}
            />
          </div>
        </div>
      )}
      {activeTab === 2 && (
        <div className={styles.insightTab}>
          <table>
            <thead>
              <tr>
                <th>Ref</th>
                <th>Status</th>
                <th>Type</th>
                <th>Sibling</th>
                <th>Start Date</th>
                <th>Last Pay Day</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member, i) => (
                <tr key={member.id} className={styles.fullWidth}>
                  <td>{member.reference}</td>
                  <td>{member.status ? member.status : 'No Status'}</td>
                  <td>{member.type ? member.type : 'No Type'}</td>
                  <td>{member.isSibling ? 'Yes' : 'No'}</td>
                  <td>
                    {member.startDate
                      ? member.startDate.substring(0, 10)
                      : 'No Date'}
                  </td>
                  <td>
                    {member.lastPayment
                      ? member.lastPayment.charge_date
                      : 'None'}
                  </td>
                  <td>
                    <button onClick={() => getMember(member, i)}>Detail</button>
                  </td>
                  <td>
                    <button onClick={() => deleteMember(member)}>Delete</button>
                  </td>
                  {i === selected ? (
                    <div>
                      {selectedMember.firstName + ' ' + selectedMember.lastName}
                    </div>
                  ) : (
                    <span />
                  )}
                  {i === selected ? (
                    <div>{selectedMember.email}</div>
                  ) : (
                    <span />
                  )}
                  {i === selected ? (
                    <div src={selectedMember.phoneNumber}> </div>
                  ) : (
                    <span />
                  )}
                  {i === selected ? (
                    <img src={selectedMember.imageFull} />
                  ) : (
                    <span />
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
        </>
      )}
    </div>
  )
}

export default Insights
